import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, Bell, Download, FileText, Loader2, RefreshCw, Search, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { classesApi, feesApi } from '../api/services.js'
import Avatar from '../components/Avatar.jsx'
import PageHeader from '../components/PageHeader.jsx'
import { formatAcademicYear, getAcademicYearOptions, getCurrentAcademicYear } from '../utils/academicYear.js'
import { formatCurrency } from '../utils/fee.js'

const DELIVERY_OPTIONS = [
  { value: 'both', label: 'Both', desc: 'Show in app and send push notification.' },
  { value: 'in_app', label: 'In-app only', desc: 'Visible in notification center only.' },
  { value: 'push', label: 'Push only', desc: 'Send a push notification only.' },
]

const getEntityId = (value) => (typeof value === 'string' ? value : value?._id || value?.id || '')
const studentName = (item) => {
  const student = item?.student || item?.studentId
  return [student?.firstName, student?.lastName].filter(Boolean).join(' ').trim() || student?.name || '—'
}
const classLabel = (classItem) => `${classItem?.name || 'Class'}${classItem?.section ? ` - ${classItem.section}` : ''}`
const getDue = (item) => Number((item.outstanding ?? item.overdueAmount ?? ((item.totalAmount || 0) - (item.paidAmount || 0))) || 0)
const getStudentId = (item) => getEntityId(item?.student || item?.studentId)

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export default function FeeDefaultersPage() {
  const navigate = useNavigate()
  const currentAcademicYear = getCurrentAcademicYear()
  const [defaulters, setDefaulters] = useState([])
  const [classes, setClasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterClass, setFilterClass] = useState('')
  const [filterAcademicYear, setFilterAcademicYear] = useState(currentAcademicYear)
  const [selected, setSelected] = useState([])
  const [reminderModal, setReminderModal] = useState(false)
  const [reminderAccount, setReminderAccount] = useState(null)
  const [reminderForm, setReminderForm] = useState({
    title: 'Fee Payment Reminder',
    message: '',
    delivery: 'both',
  })
  const [sending, setSending] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportModal, setExportModal] = useState(false)
  const [exportStep, setExportStep] = useState(1)
  const [exportScope, setExportScope] = useState('filtered')
  const [exportFormat, setExportFormat] = useState('csv')

  const fetchDefaulters = async () => {
    try {
      setLoading(true)
      const params = {}
      if (filterClass) params.classId = filterClass
      if (filterAcademicYear) params.academicYear = filterAcademicYear

      const [defaultersRes, classesRes] = await Promise.all([
        feesApi.getDefaulters(params),
        classesApi.getAll(),
      ])
      setDefaulters(Array.isArray(defaultersRes) ? defaultersRes : [])
      setClasses(Array.isArray(classesRes) ? classesRes : [])
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load defaulters')
      setDefaulters([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDefaulters()
  }, [filterClass, filterAcademicYear])

  const academicYearOptions = useMemo(() => {
    const years = [
      ...getAcademicYearOptions({ pastCount: 2, futureCount: 1 }),
      currentAcademicYear,
      ...defaulters.map((item) => formatAcademicYear(item?.academicYear)).filter(Boolean),
    ]
    return [...new Set(years)].sort((left, right) => right.localeCompare(left, 'en', { numeric: true }))
  }, [currentAcademicYear, defaulters])

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    return defaulters.filter((item) => {
      const name = studentName(item).toLowerCase()
      const admissionNo = (item.studentId?.admissionNo || item.student?.admissionNo || '').toLowerCase()
      const label = classLabel(item.classId).toLowerCase()
      const year = formatAcademicYear(item.academicYear).toLowerCase()
      const plan = String(item.structureId?.name || '').toLowerCase()
      return !query || name.includes(query) || admissionNo.includes(query) || label.includes(query) || year.includes(query) || plan.includes(query)
    })
  }, [defaulters, search])

  const filteredIds = filtered.map((item) => item._id || item.id).filter(Boolean)
  const selectedRows = filtered.filter((item) => selected.includes(item._id || item.id))
  const totalDue = filtered.reduce((sum, item) => sum + getDue(item), 0)

  const toggleSelect = (accountId) =>
    setSelected((prev) => prev.includes(accountId) ? prev.filter((item) => item !== accountId) : [...prev, accountId])

  const toggleAll = () =>
    setSelected(selected.length === filteredIds.length ? [] : filteredIds)

  const openReminder = (item = null) => {
    const targetName = item ? studentName(item) : ''
    const targetDue = item ? getDue(item) : totalDue
    const targetYear = formatAcademicYear(item?.academicYear || filterAcademicYear || '')

    setReminderAccount(item)
    setReminderForm({
      title: 'Fee Payment Reminder',
      message: item
        ? `Dear ${targetName},\n\nYour fee of ${formatCurrency(targetDue)}${targetYear ? ` for ${targetYear}` : ''} is pending. Please make the payment at the earliest.\n\nThank you.`
        : 'Dear Parent/Student,\n\nThis is a reminder that your fee payment is pending. Please clear your dues at the earliest to avoid any inconvenience.\n\nThank you.',
      delivery: 'both',
    })
    setReminderModal(true)
  }

  const handleSendReminder = async () => {
    if (!reminderForm.title.trim() || !reminderForm.message.trim()) {
      toast.error('Title and message are required')
      return
    }

    try {
      setSending(true)
      const body = {
        title: reminderForm.title.trim(),
        message: reminderForm.message.trim(),
        delivery: reminderForm.delivery,
      }

      if (reminderAccount) {
        const studentId = getStudentId(reminderAccount)
        if (!studentId) {
          toast.error('No student found for this reminder')
          return
        }
        body.studentId = studentId
        body.academicYear = formatAcademicYear(reminderAccount.academicYear || filterAcademicYear || '')
      } else {
        const sourceRows = selectedRows.length > 0 ? selectedRows : filtered
        const studentIds = [...new Set(sourceRows.map(getStudentId).filter(Boolean))]
        if (studentIds.length === 0) {
          toast.error('No defaulters to remind')
          return
        }
        body.studentIds = studentIds
        if (filterClass) body.classId = filterClass
        if (filterAcademicYear) body.academicYear = filterAcademicYear
      }

      const res = await feesApi.sendReminders(body)
      toast.success(`Reminder sent to ${res?.sent ?? (reminderAccount ? 1 : 'all')} recipient(s)`)
      setSelected([])
      setReminderModal(false)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send reminders')
    } finally {
      setSending(false)
    }
  }

  const openExportModal = () => {
    setExportStep(1)
    setExportScope('filtered')
    setExportFormat('csv')
    setExportModal(true)
  }

  const handleExport = async () => {
    try {
      setExporting(true)
      const params = { format: exportFormat }
      if (exportScope === 'filtered') {
        if (filterClass) params.classId = filterClass
        if (filterAcademicYear) params.academicYear = filterAcademicYear
        if (search.trim()) params.search = search.trim()
      }
      const response = await feesApi.exportDefaulters(params, exportFormat)
      const filename = ['fee-defaulters', exportScope === 'filtered' ? filterAcademicYear || 'all-years' : 'all-records'].filter(Boolean).join('-')

      if (exportFormat === 'pdf') {
        const { downloadAsPdf } = await import('../utils/downloadPdf.js')
        await downloadAsPdf(response.data, filename, { orientation: 'landscape' })
        toast.success('PDF downloaded')
      } else {
        downloadBlob(new Blob([response.data], { type: 'text/csv' }), `${filename}.csv`)
        toast.success('CSV downloaded')
      }
      setExportModal(false)
    } catch {
      toast.error('Export failed — try again')
    } finally {
      setExporting(false)
    }
  }

  const bulkReminderCount = selectedRows.length > 0 ? selectedRows.length : filtered.length

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fee Defaulters"
        subtitle="Same admin defaulter follow-up flow as Edubridge RN, including academic year filtering and editable reminders."
        chips={[
          loading ? 'Loading defaulters' : `${filtered.length} defaulters`,
          filterAcademicYear || 'All academic years',
          loading ? 'Calculating due' : `${formatCurrency(totalDue)} outstanding`,
        ]}
        actions={
          <>
            <button onClick={openExportModal} className="btn-secondary">
              <Download className="w-4 h-4" /> Export
            </button>
            <button onClick={fetchDefaulters} className="btn-secondary">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button onClick={() => openReminder(null)} disabled={filtered.length === 0} className="btn-primary disabled:opacity-50">
              <Bell className="w-4 h-4" />
              {selected.length > 0 ? `Remind ${selected.length}` : 'Remind All'}
            </button>
          </>
        }
      />

      {!loading && filtered.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="card p-4">
            <p className="text-xs uppercase tracking-[0.12em] text-gray-400 font-semibold">Defaulters</p>
            <p className="text-2xl font-bold text-red-600 mt-3">{filtered.length}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase tracking-[0.12em] text-gray-400 font-semibold">Outstanding</p>
            <p className="text-2xl font-bold text-gray-900 mt-3">{formatCurrency(totalDue)}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase tracking-[0.12em] text-gray-400 font-semibold">Average Due</p>
            <p className="text-2xl font-bold text-amber-600 mt-3">
              {formatCurrency(filtered.length ? Math.round(totalDue / filtered.length) : 0)}
            </p>
          </div>
        </div>
      )}

      <div className="card p-4 flex flex-wrap gap-3">
        <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search student, admission no, class, year, or fee plan..."
            className="w-full bg-transparent text-sm outline-none"
          />
        </div>
        <select className="input-field w-full sm:w-auto" value={filterAcademicYear} onChange={(event) => setFilterAcademicYear(event.target.value)}>
          <option value="">All Years</option>
          {academicYearOptions.map((year) => <option key={year} value={year}>{year}</option>)}
        </select>
        <select className="input-field w-full sm:w-auto" value={filterClass} onChange={(event) => setFilterClass(event.target.value)}>
          <option value="">All Classes</option>
          {classes.map((item) => (
            <option key={item._id || item.id} value={item._id || item.id}>
              {classLabel(item)}
            </option>
          ))}
        </select>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="table-th w-10">
                <input type="checkbox" checked={selected.length === filteredIds.length && filteredIds.length > 0} onChange={toggleAll} className="h-4 w-4 accent-primary-600" />
              </th>
              <th className="table-th">Student</th>
              <th className="table-th hidden md:table-cell">Class</th>
              <th className="table-th hidden lg:table-cell">Academic Year</th>
              <th className="table-th text-right">Outstanding</th>
              <th className="table-th hidden lg:table-cell text-right">Overdue</th>
              <th className="table-th text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              [...Array(5)].map((_, index) => (
                <tr key={index} className="animate-pulse">
                  {[...Array(7)].map((__, cellIndex) => (
                    <td key={cellIndex} className="table-td"><div className="h-4 rounded bg-gray-100" /></td>
                  ))}
                </tr>
              ))
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-12 text-center text-sm text-gray-400">
                  {search ? 'No defaulters matched your search.' : 'No defaulters found.'}
                </td>
              </tr>
            ) : (
              filtered.map((item) => {
                const accountId = item._id || item.id
                const due = getDue(item)
                const paid = item.paidAmount || 0
                const total = item.totalAmount || 0
                const paidPercent = total > 0 ? Math.round((paid / total) * 100) : 0

                return (
                  <tr key={accountId} className={selected.includes(accountId) ? 'bg-red-50/30' : 'hover:bg-gray-50'}>
                    <td className="table-td">
                      <input type="checkbox" checked={selected.includes(accountId)} onChange={() => toggleSelect(accountId)} className="h-4 w-4 accent-primary-600" />
                    </td>
                    <td className="table-td">
                      <div className="flex items-center gap-3">
                        <Avatar src={item.studentId?.avatar || item.student?.avatar} name={studentName(item)} size="sm" />
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{studentName(item)}</p>
                          <p className="text-xs text-gray-400 truncate">{item.studentId?.admissionNo || item.student?.admissionNo || 'Admission no unavailable'}</p>
                          {total > 0 && (
                            <div className="flex items-center gap-2 mt-1.5">
                              <div className="h-1.5 w-24 rounded-full bg-gray-100 overflow-hidden">
                                <div className="h-full rounded-full bg-primary-500" style={{ width: `${paidPercent}%` }} />
                              </div>
                              <span className="text-[10px] text-gray-400">{paidPercent}% paid</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="table-td hidden md:table-cell text-gray-600">{classLabel(item.classId)}</td>
                    <td className="table-td hidden lg:table-cell text-gray-600">{formatAcademicYear(item.academicYear) || '—'}</td>
                    <td className="table-td text-right font-bold text-red-600">{formatCurrency(due)}</td>
                    <td className="table-td hidden lg:table-cell text-right text-amber-600">
                      {item.overdueCount ? (
                        <span className="inline-flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5" />
                          {item.overdueCount} {item.overdueCount === 1 ? 'installment' : 'installments'}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="table-td">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => openReminder(item)} className="flex items-center gap-1 rounded-xl border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-colors">
                          <Bell className="w-3.5 h-3.5" /> Remind
                        </button>
                        <button onClick={() => navigate(`/fees/accounts/${accountId}`)} className="btn-secondary min-h-[36px] px-3 py-1.5 text-xs">
                          Open Account
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {reminderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Send Fee Reminder</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {reminderAccount ? `Reminding: ${studentName(reminderAccount)}` : `Reminding ${bulkReminderCount} defaulter${bulkReminderCount === 1 ? '' : 's'}`}
                </p>
              </div>
              <button onClick={() => setReminderModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4 max-h-[76vh] overflow-y-auto">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Notification Title</label>
                <input className="input-field w-full" value={reminderForm.title} onChange={(event) => setReminderForm((prev) => ({ ...prev, title: event.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Message</label>
                <textarea className="input-field w-full h-36 resize-none" value={reminderForm.message} onChange={(event) => setReminderForm((prev) => ({ ...prev, message: event.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-2">Delivery</label>
                <div className="grid gap-3 sm:grid-cols-3">
                  {DELIVERY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setReminderForm((prev) => ({ ...prev, delivery: opt.value }))}
                      className={`rounded-2xl border px-4 py-3 text-left transition-colors ${
                        reminderForm.delivery === opt.value ? 'border-primary-600 bg-primary-50' : 'border-gray-200 hover:border-primary-200'
                      }`}
                    >
                      <p className={`font-semibold text-sm ${reminderForm.delivery === opt.value ? 'text-primary-700' : 'text-gray-800'}`}>{opt.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 justify-end border-t border-gray-100 px-6 py-5">
              <button onClick={() => setReminderModal(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleSendReminder} disabled={sending} className="btn-primary">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                Send Reminder
              </button>
            </div>
          </div>
        </div>
      )}

      {exportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Export Defaulters</h3>
                <p className="text-sm text-gray-500 mt-1">{exportStep === 1 ? 'Step 1 of 2 — Choose records' : 'Step 2 of 2 — Choose format'}</p>
              </div>
              <button onClick={() => setExportModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            {exportStep === 1 ? (
              <div className="px-6 py-5 space-y-3">
                {[
                  { value: 'filtered', label: 'Current Filters', desc: 'Export only the records matching your current filters' },
                  { value: 'all', label: 'All Records', desc: 'Export every defaulter regardless of filters' },
                ].map((opt) => (
                  <button key={opt.value} onClick={() => setExportScope(opt.value)} className={`w-full rounded-2xl border px-4 py-3 text-left transition-colors ${exportScope === opt.value ? 'border-primary-600 bg-primary-50' : 'border-gray-200 hover:border-primary-200'}`}>
                    <p className={`font-semibold text-sm ${exportScope === opt.value ? 'text-primary-700' : 'text-gray-800'}`}>{opt.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-6 py-5 space-y-3">
                {[
                  { value: 'csv', label: 'Excel / CSV', desc: 'Open in Excel or Google Sheets', icon: FileText },
                  { value: 'pdf', label: 'PDF', desc: 'Print-ready report', icon: Download },
                ].map((opt) => {
                  const Icon = opt.icon
                  return (
                    <button key={opt.value} onClick={() => setExportFormat(opt.value)} className={`w-full flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${exportFormat === opt.value ? 'border-primary-600 bg-primary-50' : 'border-gray-200 hover:border-primary-200'}`}>
                      <Icon className={`w-5 h-5 flex-shrink-0 ${exportFormat === opt.value ? 'text-primary-600' : 'text-gray-400'}`} />
                      <div>
                        <p className={`font-semibold text-sm ${exportFormat === opt.value ? 'text-primary-700' : 'text-gray-800'}`}>{opt.label}</p>
                        <p className="text-xs text-gray-500">{opt.desc}</p>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
            <div className="flex gap-3 justify-end border-t border-gray-100 px-6 py-5">
              {exportStep === 1 ? (
                <>
                  <button onClick={() => setExportModal(false)} className="btn-secondary">Cancel</button>
                  <button onClick={() => setExportStep(2)} className="btn-primary">Next</button>
                </>
              ) : (
                <>
                  <button onClick={() => setExportStep(1)} className="btn-secondary">Back</button>
                  <button onClick={handleExport} disabled={exporting} className="btn-primary">
                    {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Download
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
